// THE FREE-SLOT CONTROL. Every half hour of the working day, with the taken ones not
// selectable — and each one saying WHY it cannot be picked.
//
// THE SERVER DECIDES, ALWAYS. This component fetches the grid and renders it; it never
// works out for itself whether a time is free. Free/busy is a rule, and the thin-client
// rule applies to it exactly as it applies to a money formula: a second opinion in the
// browser is a second opinion that will eventually differ.
//
// IT REFETCHES ON (date, partner, duration), and each of the three matters:
//   date      an obvious one.
//   partner   diaries are per partner — changing the owner changes which diary is being
//             asked, so you cannot book into the other partner's clash by switching the
//             owner last.
//   duration  a 60-minute meeting cannot start at 23:30 even though 23:30 is free. The
//             grid answers "can a meeting of THIS LENGTH start here", not "is this instant
//             free".
//
// A TAKEN SLOT IS SHOWN, NOT HIDDEN. A dropdown that silently shrinks is a dropdown nobody
// trusts — and "תפוס" tells a partner something useful, which a missing row does not.
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { meetingsApi } from '../../api/calendar';
import { ErrorText } from '../../components/Loader';

export default function SlotPicker({
  date, partnerId, withPartnerId, duration, excludeId, value, onChange, label, hint,
}) {
  const ready = Boolean(date && partnerId && duration);
  const { data, loading, error } = useAsync(
    () => (ready
      ? meetingsApi.slots(date, partnerId, duration, excludeId, withPartnerId)
      : Promise.resolve(null)),
    [date, partnerId, withPartnerId, duration, excludeId],
  );

  const slots = data ? data.slots : [];
  const anyFree = slots.some((s) => s.available);

  // FIVE STATES, EXACTLY ONE ON SCREEN AT A TIME. They were previously overlapping
  // conditions, so an error could render beneath a "checking…" line and the two together
  // read as one confused message.
  //
  // The no-date state says WHAT IS MISSING. It used to show "בודק שעות פנויות..." —
  // checking free hours — which was simply untrue: nothing was being checked, because there
  // is no day to check. A control that claims to be working while it waits for you is the
  // worst of the five, since it gives no reason to do anything.
  const body = () => {
    // No diary chosen yet (a secretary booking): the grid is somebody's, so say whose is missing.
    if (!partnerId) return <p className="muted small-note field-hint">{he.calendar.pickOwnerFirst}</p>;
    if (!ready) return <p className="muted small-note field-hint">{he.calendar.pickDateFirst}</p>;
    if (loading) return <p className="muted small-note">{he.calendar.loadingSlots}</p>;
    if (error) return <ErrorText error={error} />;
    if (!anyFree) return <p className="field-hint muted small-note">{he.calendar.noFreeSlots}</p>;
    return (
      <div className="slot-list">
        {slots.map((s) => {
          // The currently-selected time stays selectable even if the server now calls it
          // taken — that only happens on an edit, where the meeting's own slot is excluded,
          // so this is belt and braces rather than a real case. It matters because a
          // control that drops its own value shows one time and holds another.
          const selected = s.time === value;
          const usable = s.available || selected;
          return (
            <button
              type="button"
              key={s.time}
              className={`slot${selected ? ' selected' : ''}${usable ? '' : ' taken'}`}
              disabled={!usable}
              onClick={() => onChange(s.time)}
              title={usable ? undefined
                : (s.reason === 'past_end_of_day' ? he.calendar.slotPastEnd : he.calendar.slotTaken)}
            >
              {s.time}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="field slot-field">
      {/* The label names the FIELD — a time of day — and the hint says whose diary the
          answer came from. That order matters: the diary phrase used to BE the label, so
          the one control that shows clock times never said "time" anywhere on it, while
          the duration select immediately above it did. */}
      <label>{label || he.calendar.startTime}</label>
      {hint && <p className="muted small-note field-hint">{hint}</p>}
      {body()}
    </div>
  );
}
